import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { objectToHttpParams } from '@twaice-fe/frontend/shared/utilities';
import { keysToCamel, keysToSnake } from '@twaice-fe/shared/utilities';
import { DataNotFoundError, TwResponse, TWSingleResponse } from '@twaice-fe/shared/models';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private httpClient: HttpClient) {}

  fetchData<T, R>(url: string, params?: T, transformToCamel: boolean = true): Observable<R> {
    const httpParams = params ? objectToHttpParams(keysToSnake(params)) : undefined;

    return this.httpClient.get<TWSingleResponse<R>>(url, { params: httpParams }).pipe(
      map(({ data }) => {
        if (!(data as unknown)) {
          throw new DataNotFoundError(`No data found for the given parameters.`);
        }
        return transformToCamel ? keysToCamel(data) : data;
      }),
      catchError((error: Error) => {
        console.error(`Error occurred while fetching data from ${url} with params: ${params?.toString()}`, error.message);
        return throwError(() => error);
      })
    );
  }

  fetchPaginatedData<T, R>(url: string, params?: T): Observable<R> {
    const httpParams = params ? objectToHttpParams(keysToSnake(params)) : undefined;

    return this.httpClient.get<TwResponse<R>>(url, { params: httpParams }).pipe(
      map(({ data }) => {
        if (!(data as unknown)) {
          throw new DataNotFoundError(`No data found for the given parameters.`);
        }
        return keysToCamel(data);
      }),
      catchError((error: Error) => {
        console.error(`Error occurred while fetching data from ${url} with params: ${params?.toString()}`, error.message);
        return throwError(() => error);
      })
    );
  }

  createData<T, B, R>(url: string, params?: T, body?: B): Observable<R> {
    const httpParams = params ? objectToHttpParams(keysToSnake(params)) : undefined;
    const bodyKeysToSnake = body ? keysToSnake(body) : undefined;

    return this.httpClient.post<TWSingleResponse<R>>(url, bodyKeysToSnake, { params: httpParams }).pipe(
      map(({ data }) => {
        if (!(data as unknown)) {
          throw new DataNotFoundError(`No data found for the given parameters.`);
        }
        return keysToCamel(data);
      }),
      catchError((error: Error) => {
        console.error(`Error occurred while posting to ${url}`, error.message);
        return throwError(() => error);
      })
    );
  }

  updateData<T, B, R>(url: string, params?: T, body?: B): Observable<R> {
    const httpParams = params ? objectToHttpParams(keysToSnake(params)) : undefined;
    const bodyKeysToSnake = body ? keysToSnake(body) : undefined;

    return this.httpClient.put<TWSingleResponse<R>>(url, bodyKeysToSnake, { params: httpParams }).pipe(
      map(({ data }) => {
        if (!(data as unknown)) {
          throw new DataNotFoundError(`No data found for the given parameters.`);
        }
        return keysToCamel(data);
      }),
      catchError((error: Error) => {
        console.error(`Error occurred while posting to ${url}`, error.message);
        return throwError(() => error);
      })
    );
  }

  patchData<T, B, R>(url: string, params?: T, body?: B): Observable<R> {
    const httpParams = params ? objectToHttpParams(keysToSnake(params)) : undefined;
    const bodyKeysToSnake = body ? keysToSnake(body) : undefined;

    return this.httpClient.patch<TWSingleResponse<R>>(url, bodyKeysToSnake, { params: httpParams }).pipe(
      map(({ data }) => {
        if (!(data as unknown)) {
          throw new DataNotFoundError(`No data found for the given parameters.`);
        }
        return keysToCamel(data);
      }),
      catchError((error: Error) => {
        console.error(`Error occurred while posting to ${url}`, error.message);
        return throwError(() => error);
      })
    );
  }

  deleteData<T, R>(url: string, params?: T): Observable<R | null> {
    const httpParams = params ? objectToHttpParams(keysToSnake(params)) : undefined;

    return this.httpClient.delete<TWSingleResponse<R | null>>(url, { params: httpParams }).pipe(
      map((response) => {
        if (response?.data) {
          return keysToCamel(response.data);
        }

        return null;
      }),
      catchError((error: Error) => {
        console.error(`Error occurred while deleting data from ${url} with params: ${params?.toString()}`, error.message);
        return throwError(() => error);
      })
    );
  }
}
